import { useCallback, useEffect, useMemo } from 'react';
import useFilters from '@Hooks/useFilters';
import ProvidedWidgetFormFilter from '@Organisms/Dashboard/WidgetManage/WidgetFormFilter/ProvidedWidgetFormFilter';
import { ColumnField } from '../ColumnField/';
import { SortField } from '../SortField';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { convertAPIFilters } from '@Helpers/filters';
import { useListAllFields } from '@Queries/describeQueries';
import { FieldBlock } from '../FieldBlock';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THForm } from '@Molecules/THForm/THForm';
import { AVAILABLE_LIST_ENTITIES, BaseWidgetFormProps, TableEntityType } from '../FormInterface';
import { ReportTemplateWidgetEnum, SortOptions, convertToSearchQuery, logDefaultFields } from 'thehive-sdk';
import useValidateForm from '@Hooks/useValidateForm';
import { Form, Space } from 'antd';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { useLocalMessage } from '@Helpers/locale';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { ICONS } from '@Constants/iconsApp';
import { capitalizeFirstLetter } from '@Helpers/string';

import './ListForm.scss';

interface ListFormValues {
    fields: string[];
    protectData?: boolean;
    filter?: any;
    entity?: ReportTemplateWidgetEnum;
    _kind?: ReportTemplateWidgetEnum;
    sort?: SortOptions[];
    maxElements?: number;
    withTaskLogs?: boolean;
    logColumns?: string[];
}

type ListFormProps = BaseWidgetFormProps<ListFormValues>;

export const ListForm = ({ onCancel, onFinish, defaultValues }: ListFormProps) => {
    const i = useLocalMessage();
    const { form } = useValidateForm();
    const { data: describeOutput } = useListAllFields();

    const filterManager = useFilters();

    useEffect(() => {
        if (defaultValues) {
            const { _kind, filter, sort, ...values } = defaultValues;
            if (_kind) {
                values.entity = _kind;
            }

            const objectType = _kind && AVAILABLE_LIST_ENTITIES.get(_kind);
            const convertedFilter =
                (!!_kind && objectType && convertAPIFilters(filter, describeOutput?.[objectType.label])) || [];

            const convertedSort = sort?.reduce((accumulator, entry) => {
                if ('field' in entry) {
                    accumulator.push(entry);
                } else {
                    Object.entries(entry).map(([fieldName, direction]) =>
                        accumulator.push({ field: fieldName, direction }),
                    );
                }
                return accumulator;
            }, [] as any[]);

            form.setFieldsValue({ ...values, filter: convertedFilter, sort: convertedSort });
        }
    }, [defaultValues, describeOutput, form]);

    const entity = Form.useWatch<TableEntityType>(['entity'], form);

    const currentListOption = useMemo(() => (entity ? AVAILABLE_LIST_ENTITIES.get(entity) : undefined), [entity]);

    const handleResetForm = (value: any) => {
        if ('entity' in value) {
            const defaultColumns = AVAILABLE_LIST_ENTITIES.get(value.entity)?.defaultColumns || [];
            form.setFieldsValue({
                maxElements: undefined,
                fields: defaultColumns,
                sort: [],
                filter: [],
                protectData: false,
                withTaskLogs: false,
                logColumns: [],
            });
            filterManager.clearFilters();
        }
    };

    const handleFinish = useCallback(
        ({ filter, sort, ...values }: any) => {
            const returnedData = {
                ...defaultValues,
                ...values,
                _kind: values.entity,
                filter: convertToSearchQuery(filter, true),
                sort: sort ? sort.map(({ field, direction }: any) => ({ field, direction })) : [],
                logColumns: 'withTaskLogs' in values && values.withTaskLogs === true ? logDefaultFields : [],
            };

            onFinish(returnedData);
        },
        [defaultValues, onFinish],
    );

    const checkEntity = useCallback(() => {
        return entity !== undefined;
    }, [entity]);

    return (
        <THForm<ListFormValues>
            form={form}
            name="report-list-form"
            onFinish={handleFinish}
            initialValues={defaultValues}
            onValuesChange={handleResetForm}
        >
            <THForm.Layout footer={<FormSubmitButton disabled={!entity} onCancel={onCancel} />}>
                {/* Choose list type */}
                <FieldBlock
                    title={i('report-template.list.form.block.parameters')}
                    className="case-template-listForm-parameters"
                >
                    <Space align="center" direction="horizontal" size="small" className="listForm__parameters">
                        <THForm.Item
                            name="entity"
                            label={i('report-template.list.entity.label')}
                            rules={[{ required: true, message: i('report-template.list.form.entity.required') }]}
                            className="listForm__parameters-entity"
                        >
                            <THSelect
                                options={Array.from(AVAILABLE_LIST_ENTITIES).map(([value, data]) => ({
                                    value,
                                    label: capitalizeFirstLetter(
                                        i({ defaultMessage: data.label, id: `entities.${data.label}` }),
                                    ),
                                }))}
                            />
                        </THForm.Item>

                        {checkEntity() && (
                            <THForm.Item
                                name="maxElements"
                                label={i('report-template.list.max-elements.label')}
                                className="listForm__parameters-maxElement"
                            >
                                <THInputNumber placeholder={i('report-template.list.max-elements.placeholder')} />
                            </THForm.Item>
                        )}
                    </Space>

                    <THForm.Item
                        name="protectData"
                        label={i('report-template.list.fang-data.label')}
                        valuePropName="checked"
                        hidden={entity !== ReportTemplateWidgetEnum.ObservableList}
                    >
                        <THSwitch />
                    </THForm.Item>
                </FieldBlock>

                {checkEntity() && (
                    <>
                        <ColumnField
                            form={form}
                            entityType={entity}
                            name="fields"
                            title={i('report-template.list.form.block.columns')}
                            actionTitle={i('report-template.list.form.addColumn')}
                        />

                        {/* Log switch */}
                        <THForm.Item
                            name="withTaskLogs"
                            label={
                                <>
                                    <span>{i('report-template.list.log.label')}</span>
                                    <THTooltip title={i('report-template.list.log.tooltip')}>
                                        <ICONS.QuestionCircleOutlined className="listForm__withTaskLogs-tooltip-icon" />
                                    </THTooltip>
                                </>
                            }
                            valuePropName="checked"
                            hidden={entity !== ReportTemplateWidgetEnum.TaskList}
                        >
                            <THSwitch />
                        </THForm.Item>

                        <SortField
                            form={form}
                            entityType={currentListOption?.label}
                            describeOutput={describeOutput}
                            title={i('report-template.list.form.block.sort')}
                            actionTitle={i('report-template.list.form.addSort')}
                        />

                        <FieldBlock
                            title={i('report-template.list.form.block.filters')}
                            className="case-template-listForm-filters"
                        >
                            <THForm.Item name="filter" className="form-item no-value">
                                <ProvidedWidgetFormFilter
                                    entity={currentListOption?.label as string}
                                    externalFilterManager={filterManager}
                                />
                            </THForm.Item>
                        </FieldBlock>
                    </>
                )}
            </THForm.Layout>
        </THForm>
    );
};
